import React, { useMemo, useState } from "react";
import { Minus, Plus, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { RedWave } from "@/components/RedWave";

const PACKAGE_URL =
  "https://customer-assets.emergentagent.com/job_d2f1a296-f421-4ca2-ae61-9d54acaa8e8a/artifacts/y2e2iul5_Coffee%20Package%20Final.png";

const SIZES = [
  { id: "250g", label: "250 g", price: 45, subtitle: "~15 cups" },
  { id: "500g", label: "500 g", price: 80, subtitle: "~32 cups" },
];
const GRINDS = [
  { id: "whole", label: "Whole Bean" },
  { id: "ground", label: "Ground" },
];

export default function Product() {
  const [size, setSize] = useState("250g");
  const [grind, setGrind] = useState("whole");
  const [qty, setQty] = useState(1);

  const current = useMemo(() => SIZES.find((s) => s.id === size), [size]);
  const total = (current?.price || 0) * qty;

  const handleAdd = () => {
    toast.success("Added to cart", {
      description: `${qty} × ${current.label} (${
        grind === "whole" ? "Whole Bean" : "Ground"
      }) — AED ${total}.00`,
    });
  };

  return (
    <section
      id="product"
      data-testid="product-section"
      className="relative py-24 md:py-36"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14">
        {/* Product image */}
        <div className="lg:col-span-6 relative">
          <div className="relative aspect-square max-w-[540px] mx-auto">
            <div
              className="absolute inset-[10%] -z-0 rounded-full"
              style={{
                background:
                  "radial-gradient(closest-side, rgba(255,69,0,0.18), rgba(139,0,0,0.18) 40%, transparent 70%)",
                filter: "blur(16px)",
              }}
            />
            <div className="absolute -bottom-4 left-[-5%] right-[-5%] h-14 opacity-90">
              <RedWave className="w-full h-full" strokeWidth={2} />
            </div>
            <img
              src={PACKAGE_URL}
              alt="Flow State Coffee package"
              className="relative z-10 w-full h-full object-contain drop-shadow-[0_20px_60px_rgba(0,0,0,0.7)]"
              data-testid="product-image"
            />
          </div>
        </div>

        {/* Details */}
        <div className="lg:col-span-6">
          <span className="text-[10px] tracking-[0.32em] uppercase text-[color:var(--fs-gold-muted)]">
            06 / The Blend
          </span>
          <h2
            className="mt-4 font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-white"
            data-testid="product-heading"
          >
            Flow State
            <br />
            <span className="italic text-gold-foil">Reserve Blend.</span>
          </h2>

          <p className="mt-6 text-neutral-400 text-base leading-relaxed max-w-md">
            Medium-dark roast, chocolate & nutty profile, low acidity. Built for
            long focus sessions and slow mornings.
          </p>

          <div className="mt-10">
            <div className="text-[10px] tracking-[0.28em] uppercase text-neutral-500 mb-3">
              Size
            </div>
            <div className="grid grid-cols-2 gap-3 max-w-md">
              {SIZES.map((s) => (
                <button
                  key={s.id}
                  className="variant-pill text-left"
                  data-active={size === s.id}
                  onClick={() => setSize(s.id)}
                  data-testid={`size-${s.id}`}
                >
                  <div className="font-display text-xl">{s.label}</div>
                  <div className="text-[10px] tracking-[0.22em] uppercase mt-1 opacity-70">
                    {s.subtitle} · AED {s.price}
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="mt-8">
            <div className="text-[10px] tracking-[0.28em] uppercase text-neutral-500 mb-3">
              Grind
            </div>
            <div className="grid grid-cols-2 gap-3 max-w-md">
              {GRINDS.map((g) => (
                <button
                  key={g.id}
                  className="variant-pill"
                  data-active={grind === g.id}
                  onClick={() => setGrind(g.id)}
                  data-testid={`grind-${g.id}`}
                >
                  <div className="font-display text-lg">{g.label}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Qty + Add */}
          <div className="mt-10 flex flex-wrap items-center gap-5">
            <div
              className="flex items-center gap-4 border border-[color:var(--fs-hairline)] rounded-full px-4 py-2"
              data-testid="qty-stepper"
            >
              <button
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                className="text-[color:var(--fs-gold-muted)] hover:text-[color:var(--fs-gold-hi)]"
                data-testid="qty-decrement"
                aria-label="Decrease quantity"
              >
                <Minus size={14} />
              </button>
              <span
                className="font-display text-lg text-white min-w-[1.5rem] text-center"
                data-testid="qty-display"
              >
                {qty}
              </span>
              <button
                onClick={() => setQty((q) => q + 1)}
                className="text-[color:var(--fs-gold-muted)] hover:text-[color:var(--fs-gold-hi)]"
                data-testid="qty-increment"
                aria-label="Increase quantity"
              >
                <Plus size={14} />
              </button>
            </div>

            <button
              onClick={handleAdd}
              className="btn-gold"
              data-testid="product-add-cta"
            >
              Add to Cart · AED {total}
              <ArrowRight size={14} />
            </button>
          </div>

          <div className="mt-10 hairline-gold" />
          <div className="mt-6 flex flex-wrap gap-x-10 gap-y-2 text-[11px] tracking-[0.22em] uppercase text-neutral-500">
            <span>Free shipping over 150 AED</span>
            <span>Roasted to order</span>
            <span>30-day promise</span>
          </div>
        </div>
      </div>
    </section>
  );
}
